import { useTranslation, Trans } from "next-i18next";
import parse from "html-react-parser";
import {
    Docker,
    K8s, Linux, Windows
} from "../../components/v2/icons";``
import AgentIntegration from "../../components/v2/agent-integration";
import Accordion from "../../components/v2/accordion/accordion";
import ProductResources from "../../components/v2/product/product-resource";
import ProductHeroSection from "../../components/v2/common/product-hero";
import ProductTour from "../../components/v2/product/product-tour";
import '../../styles/scss/_log-tab.module.scss';
import '../../styles/scss/_faq-section.module.scss';
import ProductOverviewSection from "../../components/v2/product-overview-section";
import { APM, InfrastructureIcon, OpsAI } from "../../components/v2/icons-v2";
import FeaturesMonitoringV2 from "../../components/v2/tabs/features-monitoring-v2";
import CallToActionDocsLink from "../../components/v2/blog/call-to-action-docs-link";
import ClientTabsTestimonials from "../../components/v2/mw-carousel/testimonial-with-tabs";

export default function LogMonitoring() {
    const { t } = useTranslation("common");
    const featuresData = t('product.log-monitoring.features.detailedFeatures', { returnObjects: true }) as any[]
    const features = Array.isArray(featuresData) ? featuresData : []

    const faqsData = t('product.log-monitoring.faq.questions', { returnObjects: true }) as any[]
    const faqs = Array.isArray(faqsData) ? faqsData : []

    const resourceData = t('product.log-monitoring.resources', { returnObjects: true }) as any[]
    const resource = Array.isArray(resourceData) ? resourceData : []

    const productTourData = t('product.log-monitoring.productTour', { returnObjects: true }) as any

    const agentIntegrationData = t('product.log-monitoring.agentIntegration', { returnObjects: true }) as any

    const agentDeployData = t('product.shared.agentDeploy', { returnObjects: true }) as any[]
    const agentDeployArray = Array.isArray(agentDeployData) ? agentDeployData : []

    const agentDeploy = agentDeployArray.map(agent => ({
        ...agent,
        icon: agent.key === 'kubernetes' ? <K8s /> :
            agent.key === 'linux' ? <Linux /> :
                agent.key === 'docker' ? <Docker /> :
                    agent.key === 'windows' ? <Windows /> :
                        <span className={"text-gradient"}>450+</span>
    }))

    const productOverviewData = {
        title: "Product overview",
        buttons: [
            {
                label: "View Docs",
                link: "https://docs.middleware.io/workflow/log-monitoring/overview",
                className: "ahref-btn primary",
            },
            {
                label: "View Pricing",
                link: "/pricing/",
                className: "ahref-btn secondary",
            },
        ],
        description:
            "Middleware Log Monitoring unifies logs from every layer of your stack with AI-powered management and deep context. With complete visibility and the ability to correlate logs with traces and metrics in a single pane of glass, you can pinpoint root causes instantly, slash MTTR, and optimize overall performance. Achieve enterprise-grade observability and 100% data control at a fraction of the price of legacy solutions.",
        worksWellWithTitle: "Works Well With",
        worksWellWith: [
            {
                title: "APM",
                link: "/product/apm/",
                icon: <APM />
            },
            {
                title: "Infrastructure",
                link: "/product/infrastructure-monitoring/",
                icon: <InfrastructureIcon />
            },
            {
                title: "OpsAI",
                link: "/product/ops-ai/",
                icon: <OpsAI />
            },
        ],
    };

    return (
        <>
            <ProductHeroSection title={<h1>{t('product.log-monitoring.hero.title')} <br /><span>{t('product.log-monitoring.hero.titleHighlight')}</span></h1>}
                description={t('product.log-monitoring.hero.description')}
                img={"/images/v2/products/Log-Monitoring.webp"} imgMobile={"/images/v2/products/Log-Monitoring-mobile.webp"} imgAlt={t('product.log-monitoring.hero.imageAlt')} preloadImg hasActions={true} tips={t('product.log-monitoring.hero.tips')} />


            <ProductOverviewSection productOverviewData={productOverviewData} />

            <FeaturesMonitoringV2 productFeatures={features} tourId={"logs-tour"} />

            <CallToActionDocsLink
                            title={'Start Log Monitoring Free and Debug Faster Today'}
                            docsLink="https://docs.middleware.io/workflow/log-monitoring/overview"
                        />

            <section className="testimonial-section">
                <div className="container">
                    <div className="section-tag testimonial">{t("testimonials.tag")} 👤</div>
                    <h2>Hear it from the best: Why <span className="text-gradient">top companies</span> trust Middleware</h2>

                    <ClientTabsTestimonials />
                </div>
            </section>

            <ProductTour tourSrc={"https://app.supademo.com/embed/clzgse1th14cl9x776xgkr6w4?embed_v=2"} title={productTourData.title} tourId={"log-tour"} description={productTourData.description} titleChanged={<h2>Take an <span className="text-gradient">Interactive Tour </span> for Log Monitoring</h2>} />

            <AgentIntegration
                agentDeploy={agentDeploy}
                title={
                    <h2>
                        <Trans
                            i18nKey="product.log-monitoring.agentIntegration.title"
                            components={{ highlight: <span className="text-gradient" /> }}
                        />
                    </h2>
                }
                description={"Collect logs from applications, servers, and cloud services with minimal setup, no complex configuration required. Get real-time visibility into logs to search, analyze, and troubleshoot issues faster."}
                contactUsVariant="log-monitoring"
            />

            <ProductResources resources={resource} title={t('product.shared.resources.title')} />

            <section className={"faq-section"}>
                <div className={"container"}>
                    <h2>{t('product.log-monitoring.faq.title')}</h2>
                    <p>{t('product.log-monitoring.faq.subtitle')}</p>
                    <div className={"faq-item"}>
                        <Accordion faqs={faqs.map(faq => ({
                            ...faq,
                            answerPlain: faq.text,
                            text:
                                faq.textHtml != null && faq.textHtml !== ""
                                    ? parse(faq.textHtml)
                                    : <p>{faq.text}</p>
                        }))} showGroups={true} />
                    </div>
                </div>
            </section>
        </>
    )
}